import type { Database } from '../../design/services';
import type { Entity, Model } from '../../design/types';
import { MODELS, type RootModels } from './root';

// ── Shared action templates ──

/** Generates notification code lines for use in action code strings */
const notifyCode = (userExpr: string, title: string, bodyExpr: string, p: string) => [
  `var ${p}Mg = await app.meta.database.create("/messages", { title: "${title}", body: ${bodyExpr}, read: false })`,
  `var ${p}Ns = await app.meta.database.list("/notifications", [], { user: ${userExpr} })`,
  `if (${p}Ns.length) { var ${p}Ms = ${p}Ns[0].messages || []; ${p}Ms.push(${p}Mg.id); await app.meta.database.update("/notifications", ${p}Ns[0].id, { messages: ${p}Ms }) }`,
  `else { await app.meta.database.create("/notifications", { user: ${userExpr}, messages: [${p}Mg.id] }) }`,
];

// ── Shared action code generators ──

const placeOrderCode = (basePath: string) => [
  'var items = await app.database.list("/order-items", [], { order: page.id })',
  'if (!items.length) throw new Error("Order has no items")',
  'var periods = await app.database.list("/order-periods", [], { active: true })',
  'if (periods.length) {',
  '  var today = new Date().toISOString().split("T")[0]',
  '  if (today > periods[0].endDate) throw new Error("Order period has ended (" + periods[0].endDate + ")")',
  '}',
  'var totalPointCost = 0',
  'var i, orderItem, catItemId, catItem',
  'for (i = 0; i < items.length; i++) {',
  '  orderItem = items[i]',
  '  catItemId = typeof orderItem.catalogItem === "object" ? orderItem.catalogItem.id : orderItem.catalogItem',
  '  catItem = await app.database.get("/catalog", catItemId)',
  '  if (catItem.stockQuantity !== undefined && catItem.stockQuantity <= 0) throw new Error(catItem.name + " is out of stock")',
  '  if (orderItem.size && catItem.sizes) {',
  '    var validSizes = catItem.sizes.split(",").map(function(s) { return s.trim() })',
  '    if (validSizes.indexOf(orderItem.size) === -1) throw new Error("Invalid size for " + catItem.name + ". Available: " + catItem.sizes)',
  '  }',
  '  totalPointCost += (catItem.pointCost || 0) * (orderItem.quantity || 1)',
  '}',
  'if (totalPointCost <= 0) throw new Error("Invalid order total")',
  'var account = await app.database.get("/users", app.user.id)',
  'if (page.data.shipTo && account.locations && account.locations.length) {',
  '  var locs = account.locations.map ? account.locations.map(function(l) { return typeof l === "object" ? l.id : l }) : [account.locations]',
  '  var shipId = page.data.shipTo.id',
  '  if (locs.indexOf(shipId) === -1) throw new Error("Ship-to location is not in your assigned locations")',
  '}',
  'var remaining = account.pointsAllotment - account.pointsSpent',
  'if (totalPointCost > remaining) throw new Error("Insufficient points. Need " + totalPointCost + ", have " + remaining)',
  'await app.database.update("/users", account.id, { pointsSpent: account.pointsSpent + totalPointCost })',
  'for (i = 0; i < items.length; i++) {',
  '  orderItem = items[i]',
  '  catItemId = typeof orderItem.catalogItem === "object" ? orderItem.catalogItem.id : orderItem.catalogItem',
  '  catItem = await app.database.get("/catalog", catItemId)',
  '  var itemCost = (catItem.pointCost || 0) * (orderItem.quantity || 1)',
  '  await app.database.update("/order-items", orderItem.id, { pointCost: itemCost })',
  '  if (catItem.stockQuantity !== undefined) {',
  '    await app.database.update("/catalog", catItemId, { stockQuantity: catItem.stockQuantity - (orderItem.quantity || 1) })',
  '  }',
  '  if (catItem.backorderDate && catItem.stockQuantity !== undefined && catItem.stockQuantity - (orderItem.quantity || 1) < 0) {',
  ...notifyCode('app.user.id', 'Item on Backorder', 'catItem.name + " expected availability: " + catItem.backorderDate', 'bo'),
  '  }',
  '}',
  'await app.database.update(page.model, page.id, { totalPointCost: totalPointCost, status: "Placed", orderDate: new Date().toISOString().split("T")[0] })',
  ...notifyCode('app.user.id', 'Order Confirmed', '"Your order of " + items.length + " items has been placed and is pending approval."', 'sf'),
  'if (account.approver) {',
  ...notifyCode('account.approver', 'Order Placed', '"A new order of " + items.length + " items requires your approval."', 'ap'),
  '}',
  `app.navigate("${basePath}/" + page.id)`,
].join('\n');

const cancelOrderCode = (basePath: string, userExpr: string) => [
  'var orderItems = await app.database.list("/order-items", [], { order: page.id })',
  'for (var i = 0; i < orderItems.length; i++) {',
  '  var oi = orderItems[i]',
  '  var ciId = typeof oi.catalogItem === "object" ? oi.catalogItem.id : oi.catalogItem',
  '  try {',
  '    var ci = await app.database.get("/catalog", ciId)',
  '    if (ci.stockQuantity !== undefined) {',
  '      await app.database.update("/catalog", ciId, { stockQuantity: ci.stockQuantity + (oi.quantity || 1) })',
  '    }',
  '  } catch(e) {}',
  '}',
  `var account = await app.database.get("/users", ${userExpr})`,
  'await app.database.update("/users", account.id, { pointsSpent: Math.max(0, account.pointsSpent - (page.data.totalPointCost || 0)) })',
  'await app.database.update(page.model, page.id, { status: "Cancelled" })',
  'if (account.approver) {',
  ...notifyCode('account.approver', 'Order Cancelled', '"An order has been cancelled by " + (account.name || "a user") + "."', 'cn'),
  '}',
  `app.navigate("${basePath}")`,
].join('\n');

// ══════════════════════════════════════════════════════════════════════════════
// BOOTSTRAP
// ══════════════════════════════════════════════════════════════════════════════

export interface OrderingModels {
  orderingUserModel: Entity;
  categoriesModel: Entity;
  catalogModel: Entity;
  locationsModel: Entity;
  ordersModel: Entity;
  orderPeriodsModel: Entity;
  orderItemsModel: Entity;
}

/** Seeds ordering meta DB with models, properties, groups, capabilities, authorizations */
export async function bootstrapOrdering(orderingMetaDb: Database, rootUsers: Entity[], rootModels: RootModels, orderingProjectId: string): Promise<{ models: OrderingModels, bramptonProjectId: string, vancouverProjectId: string }> {
  const M = MODELS as unknown as Model;
  const { propertyModel, actionsModel, groupModel, projectModel, capabilityModel, authorizationModel } = rootModels;
  const P = propertyModel as unknown as Model;
  const A = actionsModel as unknown as Model;

  // Helper: create CRUD action entities (Save + Delete)
  const createCrud = async () => {
    const [save, del] = await Promise.all([
      orderingMetaDb.create(A, { name: 'Save', code: 'app.database.update(page.model, page.id, page.data).then(() => app.navigate(page.model.path))' }),
      orderingMetaDb.create(A, { name: 'Delete', code: 'await app.database.delete(page.model, page.id); app.navigate(page.model.path)', hidden: 'return !this.id' }),
    ]);
    return [save.id, del.id];
  };

  // ── Phase 1a: Create models (no cross-references, no inline actions) ──

  const [orderingHomeModel, orderingUserModel, categoriesModel, catalogModel, locationsModel, ordersModel, orderPeriodsModel, orderItemsModel] = await Promise.all([
    orderingMetaDb.create(M, { name: 'Home', singular: 'Home', type: 'singleton', path: '/', icon: 'Home', actions: [] }),
    orderingMetaDb.create(M, {
      name: 'Users', singular: 'user', type: 'collection', path: '/users', icon: 'User',
      label: 'return this.name',
      actions: [],
    }),
    orderingMetaDb.create(M, { name: 'Categories', singular: 'category', type: 'collection', path: '/categories', icon: 'Tag', label: 'return this.name', actions: [] }),
    orderingMetaDb.create(M, {
      name: 'Catalog', singular: 'catalog item', type: 'collection', path: '/catalog', icon: 'Shirt',
      label: 'return this.name', view: 'card',
      actions: [],
    }),
    orderingMetaDb.create(M, { name: 'Locations', singular: 'location', type: 'collection', path: '/locations', icon: 'MapPin', label: 'return this.name', actions: [] }),
    orderingMetaDb.create(M, {
      name: 'Orders', singular: 'order', type: 'collection', path: '/orders', icon: 'ShoppingCart',
      label: 'return (this.status || "Draft") + " - " + (this.orderDate || "New")',
      actions: [],
    }),
    orderingMetaDb.create(M, { name: 'Order Periods', singular: 'order period', type: 'collection', path: '/order-periods', icon: 'Calendar', label: 'return this.name', actions: [] }),
    orderingMetaDb.create(M, {
      name: 'Order Items', singular: 'order item', type: 'collection', path: '/order-items', icon: 'Package',
      label: 'return this.catalogItem?.name || "Item"',
      actions: [],
    }),
  ]);

  // ── Phase 1b: Create models with references to other models ──

  const [myAccountModel, myOrdersModel] = await Promise.all([
    orderingMetaDb.create(M, {
      name: 'My Account', singular: 'account', type: 'personal', path: '/my-account', icon: 'CircleUser',
      label: 'return "My Uniform Account"', reference: orderingUserModel.id,
      actions: [],
    }),
    orderingMetaDb.create(M, {
      name: 'My Orders', singular: 'order', type: 'personal', path: '/my-orders', icon: 'ShoppingCart',
      label: 'return (this.status || "Draft") + " - " + (this.orderDate || "New")', reference: ordersModel.id,
      actions: [],
    }),
  ]);

  const allDomainModels = [orderingHomeModel, categoriesModel, catalogModel, locationsModel, ordersModel, myAccountModel, orderPeriodsModel, myOrdersModel, orderItemsModel];
  const readModels = [categoriesModel, catalogModel, locationsModel, orderPeriodsModel, orderingUserModel];

  // ── Create action entities ──

  // Users: CRUD + Reset Points
  const [usersCrud, resetPoints] = await Promise.all([
    createCrud(),
    orderingMetaDb.create(A, { name: 'Reset Points', code: 'await app.database.update(page.model, page.id, { pointsSpent: 0 }); app.navigate(page.model.path)', hidden: 'return !this.id' }),
  ]);
  const usersActionIds = [...usersCrud, resetPoints.id];

  // Categories, Locations, Order Periods: CRUD each
  const [categoriesCrud, locationsCrud, orderPeriodsCrud] = await Promise.all([
    createCrud(),
    createCrud(),
    createCrud(),
  ]);

  // Catalog: CRUD + Order This Item
  const [catalogCrud, orderThisItem] = await Promise.all([
    createCrud(),
    orderingMetaDb.create(A, {
      name: 'Order This Item',
      code: [
        'var drafts = await app.database.list("/orders", [], { user: app.user.id, status: "Draft" })',
        'var order = drafts.length ? drafts[0] : await app.database.create("/orders", { user: app.user.id, status: "Draft" })',
        'await app.database.create("/order-items", { order: order.id, catalogItem: page.id, quantity: 1 })',
        'app.navigate("/my-orders/" + order.id)',
      ].join('\n'),
      hidden: 'return !this.id || !this.active',
    }),
  ]);
  const catalogActionIds = [...catalogCrud, orderThisItem.id];

  // Orders: Save Draft, Place Order, Approve, Ship, Confirm Receipt, Cancel Order, Delete
  const [ordersSaveDraft, ordersPlaceOrder, ordersApprove, ordersShip, ordersConfirmReceipt, ordersCancelOrder, ordersDelete] = await Promise.all([
    orderingMetaDb.create(A, {
      name: 'Save Draft',
      code: [
        'var ids = page.data.items || []',
        'for (var i = 0; i < ids.length; i++) {',
        '  try { await app.database.update("/order-items", ids[i].id || ids[i], {}) }',
        '  catch(e) {}',
        '}',
        'await app.database.update(page.model, page.id, page.data)',
        'app.navigate(page.model.path)',
      ].join('\n'),
      hidden: 'return this.status !== "Draft" && this.status !== undefined',
    }),
    orderingMetaDb.create(A, {
      name: 'Place Order',
      code: placeOrderCode('/orders'),
      hidden: 'return this.status !== "Draft"',
    }),
    orderingMetaDb.create(A, {
      name: 'Approve',
      code: [
        'await app.database.update(page.model, page.id, { status: "Approved" })',
        ...notifyCode('page.data.user.id || page.data.user', 'Order Approved', '"Your order has been approved."', 'av'),
        'app.navigate(page.model.path)',
      ].join('\n'),
      hidden: 'return this.status !== "Placed"',
    }),
    orderingMetaDb.create(A, {
      name: 'Ship',
      code: [
        'await app.database.update(page.model, page.id, { status: "Shipped", trackingNumber: page.data.trackingNumber || "" })',
        ...notifyCode('page.data.user.id || page.data.user', 'Order Shipped', '"Your order has been shipped." + (page.data.trackingNumber ? " Tracking: " + page.data.trackingNumber : "")', 'sh'),
        'app.navigate(page.model.path)',
      ].join('\n'),
      hidden: 'return this.status !== "Approved"',
    }),
    orderingMetaDb.create(A, {
      name: 'Confirm Receipt',
      code: [
        'await app.database.update(page.model, page.id, { status: "Received" })',
        'app.navigate(page.model.path)',
      ].join('\n'),
      hidden: 'return this.status !== "Shipped"',
    }),
    orderingMetaDb.create(A, {
      name: 'Cancel Order',
      code: cancelOrderCode('/orders', '(page.data.user && page.data.user.id) || app.user.id'),
      hidden: 'return this.status === "Shipped" || this.status === "Received" || this.status === "Cancelled" || this.status === "Draft" || !this.id',
    }),
    orderingMetaDb.create(A, {
      name: 'Delete',
      code: 'await app.database.delete(page.model, page.id); app.navigate(page.model.path)',
      hidden: 'return !this.id || this.status !== "Draft"',
    }),
  ]);
  const ordersActionIds = [ordersSaveDraft.id, ordersPlaceOrder.id, ordersApprove.id, ordersShip.id, ordersConfirmReceipt.id, ordersCancelOrder.id, ordersDelete.id];

  // Order Items: Add to Order, Remove
  const [oiAddToOrder, oiRemove] = await Promise.all([
    orderingMetaDb.create(A, {
      name: 'Add to Order',
      code: [
        'await app.database.update(page.model, page.id, page.data)',
        'var segs = window.location.pathname.split("/").filter(Boolean)',
        'var parentId = segs[1]',
        'var propName = segs[2]',
        'var parent = await app.database.get("/orders", parentId)',
        'var existing = parent[propName]',
        'if (Array.isArray(existing)) {',
        '  if (existing.indexOf(page.id) === -1) await app.database.update("/orders", parentId, { [propName]: existing.concat(page.id) })',
        '} else {',
        '  await app.database.update("/orders", parentId, { [propName]: page.id })',
        '}',
        'var parentPath = window.location.pathname.split("/").slice(0, -2).join("/")',
        'app.navigate(parentPath)',
      ].join('\n'),
    }),
    orderingMetaDb.create(A, {
      name: 'Remove',
      code: [
        'var parentPath = window.location.pathname.split("/").slice(0, -2).join("/")',
        'var segs = window.location.pathname.split("/").filter(Boolean)',
        'var parentId = segs[1]',
        'var parent = await app.database.get("/orders", parentId)',
        'var items = (parent.items || []).filter(function(i) { return (typeof i === "object" ? i.id : i) !== page.id })',
        'await app.database.update("/orders", parentId, { items: items })',
        'await app.database.delete(page.model, page.id)',
        'app.navigate(parentPath)',
      ].join('\n'),
      hidden: 'return !this.id',
    }),
  ]);
  const orderItemsActionIds = [oiAddToOrder.id, oiRemove.id];

  // My Account: Just Save (no navigate)
  const myAccountSave = await orderingMetaDb.create(A, { name: 'Save', code: 'app.database.update(page.model, page.id, page.data)' });
  const myAccountActionIds = [myAccountSave.id];

  // My Orders: Save Draft, Place Order, Confirm Receipt, Cancel Order, Delete
  const [myOrdersSaveDraft, myOrdersPlaceOrder, myOrdersConfirmReceipt, myOrdersCancelOrder, myOrdersDelete] = await Promise.all([
    orderingMetaDb.create(A, {
      name: 'Save Draft',
      code: [
        'page.data.user = app.user.id',
        'var ids = page.data.items || []',
        'for (var i = 0; i < ids.length; i++) {',
        '  try { await app.database.update("/order-items", ids[i].id || ids[i], {}) }',
        '  catch(e) {}',
        '}',
        'await app.database.update(page.model, page.id, page.data)',
        'app.navigate(page.model.path)',
      ].join('\n'),
      hidden: 'return this.status !== "Draft" && this.status !== undefined',
    }),
    orderingMetaDb.create(A, {
      name: 'Place Order',
      code: placeOrderCode('/my-orders'),
      hidden: 'return this.status !== "Draft"',
    }),
    orderingMetaDb.create(A, {
      name: 'Confirm Receipt',
      code: [
        'await app.database.update(page.model, page.id, { status: "Received" })',
        'app.navigate(page.model.path)',
      ].join('\n'),
      hidden: 'return this.status !== "Shipped"',
    }),
    orderingMetaDb.create(A, {
      name: 'Cancel Order',
      code: cancelOrderCode('/my-orders', 'app.user.id'),
      hidden: 'return this.status === "Shipped" || this.status === "Received" || this.status === "Cancelled" || this.status === "Draft" || !this.id',
    }),
    orderingMetaDb.create(A, {
      name: 'Delete',
      code: 'await app.database.delete(page.model, page.id); app.navigate(page.model.path)',
      hidden: 'return !this.id || this.status !== "Draft"',
    }),
  ]);
  const myOrdersActionIds = [myOrdersSaveDraft.id, myOrdersPlaceOrder.id, myOrdersConfirmReceipt.id, myOrdersCancelOrder.id, myOrdersDelete.id];

  // ── Phase 2: Create properties ──

  // User properties
  const [userName, userEmail, userPic, userActive] = await Promise.all([
    orderingMetaDb.create(P, { name: 'name', type: 'string' }),
    orderingMetaDb.create(P, { name: 'email', type: 'string' }),
    orderingMetaDb.create(P, { name: 'pic', type: 'image' }),
    orderingMetaDb.create(P, { name: 'active', type: 'boolean', default: 'return true' }),
  ]);

  // Staff-account properties
  const [staffPointsAllotment, staffPointsSpent, staffPointsRemaining, staffDepartment, staffLocations, staffApprover, staffRole] = await Promise.all([
    orderingMetaDb.create(P, { name: 'pointsAllotment', type: 'integer' }),
    orderingMetaDb.create(P, { name: 'pointsSpent', type: 'integer', default: 'return 0' }),
    orderingMetaDb.create(P, { name: 'pointsRemaining', type: 'integer', computed: 'return this.pointsAllotment - this.pointsSpent' }),
    orderingMetaDb.create(P, { name: 'department', type: 'string', enum: 'return ["Aquatics","Community Centres","Concessions","Customer Service","Fitness","General & Camp","Gymnastics","Operations","Skate","Volunteers"]' }),
    orderingMetaDb.create(P, { name: 'locations', type: 'reference', reference: locationsModel.id, array: true }),
    orderingMetaDb.create(P, { name: 'approver', type: 'reference', reference: orderingUserModel.id }),
    orderingMetaDb.create(P, { name: 'role', type: 'string', enum: 'return ["End User","Approver","Admin"]', default: 'return "End User"' }),
  ]);

  // Category properties
  const [catName, catDescription] = await Promise.all([
    orderingMetaDb.create(P, { name: 'name', type: 'string' }),
    orderingMetaDb.create(P, { name: 'description', type: 'string', multiline: 3 }),
  ]);

  // Catalog properties
  const [catalogName, catalogDescription, catalogCategory, catalogPointCost, catalogImage, catalogSizes, catalogActive, catalogStockQuantity, catalogInStock, catalogBackorderDate, catalogDepartments, catalogLogoOptions] = await Promise.all([
    orderingMetaDb.create(P, { name: 'name', type: 'string' }),
    orderingMetaDb.create(P, { name: 'description', type: 'string', multiline: 3 }),
    orderingMetaDb.create(P, { name: 'category', type: 'reference', reference: categoriesModel.id }),
    orderingMetaDb.create(P, { name: 'pointCost', type: 'integer' }),
    orderingMetaDb.create(P, { name: 'image', type: 'image' }),
    orderingMetaDb.create(P, { name: 'sizes', type: 'string' }),
    orderingMetaDb.create(P, { name: 'active', type: 'boolean', default: 'return true' }),
    orderingMetaDb.create(P, { name: 'stockQuantity', type: 'integer' }),
    orderingMetaDb.create(P, { name: 'inStock', type: 'boolean', computed: 'return this.stockQuantity > 0' }),
    orderingMetaDb.create(P, { name: 'backorderDate', type: 'date' }),
    orderingMetaDb.create(P, { name: 'departments', type: 'string', enum: 'return ["Aquatics","Community Centres","Concessions","Customer Service","Fitness","General & Camp","Gymnastics","Operations","Skate","Volunteers"]', array: true }),
    orderingMetaDb.create(P, { name: 'logoOptions', type: 'string', multiline: 2 }),
  ]);

  // Location properties
  const [locName, locAddress] = await Promise.all([
    orderingMetaDb.create(P, { name: 'name', type: 'string' }),
    orderingMetaDb.create(P, { name: 'address', type: 'string' }),
  ]);

  // Order properties (no more item-level props — moved to Order Items)
  const [orderUser, orderItems, orderTotalPointCost, orderStatus, orderDate, orderShipTo, orderNotes, orderTrackingNumber] = await Promise.all([
    orderingMetaDb.create(P, { name: 'user', type: 'reference', reference: orderingUserModel.id, default: 'return app.user' }),
    orderingMetaDb.create(P, { name: 'items', type: 'object', reference: orderItemsModel.id, array: true }),
    orderingMetaDb.create(P, { name: 'totalPointCost', type: 'integer', computed: 'return this.items ? this.items.reduce(function(total, item) { return total + (item.pointCost || 0) }, 0) : 0' }),
    orderingMetaDb.create(P, { name: 'status', type: 'string', enum: 'return ["Draft","Placed","Approved","Shipped","Received","Cancelled"]', default: 'return "Draft"' }),
    orderingMetaDb.create(P, { name: 'orderDate', type: 'date' }),
    orderingMetaDb.create(P, { name: 'shipTo', type: 'reference', reference: locationsModel.id }),
    orderingMetaDb.create(P, { name: 'notes', type: 'string', multiline: 3 }),
    orderingMetaDb.create(P, { name: 'trackingNumber', type: 'string' }),
  ]);

  // Order Item properties
  const [oiCatalogItem, oiQuantity, oiSize, oiPointCost, oiLogoChoice] = await Promise.all([
    orderingMetaDb.create(P, { name: 'catalogItem', type: 'reference', reference: catalogModel.id, filter: 'return { active: true }' }),
    orderingMetaDb.create(P, { name: 'quantity', type: 'integer', default: 'return 1' }),
    orderingMetaDb.create(P, { name: 'size', type: 'string' }),
    orderingMetaDb.create(P, { name: 'pointCost', type: 'integer' }),
    orderingMetaDb.create(P, { name: 'logoChoice', type: 'string' }),
  ]);

  // My-orders user (hidden ref for personal model linkage)
  const myOrderUser = await orderingMetaDb.create(P, { name: 'user', type: 'reference', reference: orderingUserModel.id, hidden: 'return true' });

  // Order period properties
  const [periodName, periodStartDate, periodEndDate, periodActive] = await Promise.all([
    orderingMetaDb.create(P, { name: 'name', type: 'string' }),
    orderingMetaDb.create(P, { name: 'startDate', type: 'date' }),
    orderingMetaDb.create(P, { name: 'endDate', type: 'date' }),
    orderingMetaDb.create(P, { name: 'active', type: 'boolean', default: 'return true' }),
  ]);

  // Ordering home welcome property (enhanced with instructions + category browsing)
  const orderingWelcome = await orderingMetaDb.create(P, { name: 'welcome', type: 'custom', code: [
    'function({ React, app, navigate }) {',
    '  var h = React.createElement',
    '  var _s = React.useState(null)',
    '  var account = _s[0], setAccount = _s[1]',
    '  var _s2 = React.useState(null)',
    '  var period = _s2[0], setPeriod = _s2[1]',
    '  var _s3 = React.useState([])',
    '  var categories = _s3[0], setCategories = _s3[1]',
    '  React.useEffect(function() {',
    '    app.database.get("/users", app.user.id).then(function(a) { setAccount(a) })',
    '    app.database.list("/order-periods", [], { active: true }).then(function(p) { if (p.length) setPeriod(p[0]) })',
    '    app.database.list("/categories").then(function(c) { setCategories(c) })',
    '  }, [])',
    '  return h("div", { style: { width: "100%" } },',
    '    h("h2", null, "Welcome to the " + (app.project && app.project.name || "Uniform") + " Uniform Ordering System"),',
    '    h("p", null, "Browse the catalog, place orders, and track your order status."),',
    '    h("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem", margin: "1.5rem 0" } },',
    '      account && h("div", { style: { padding: "1rem", border: "1px solid #e0e0e0", borderRadius: 8 } },',
    '        h("h3", { style: { margin: "0 0 0.5rem" } }, "Your Points"),',
    '        h("p", { style: { fontSize: 24, fontWeight: 600, margin: 0 } }, (account.pointsAllotment - account.pointsSpent) + " / " + account.pointsAllotment),',
    '        h("p", { style: { opacity: 0.7, margin: "0.25rem 0 0" } }, "Points remaining")',
    '      ),',
    '      period && h("div", { style: { padding: "1rem", border: "1px solid #e0e0e0", borderRadius: 8 } },',
    '        h("h3", { style: { margin: "0 0 0.5rem" } }, "Current Order Period"),',
    '        h("p", { style: { fontWeight: 500, margin: 0 } }, period.name),',
    '        h("p", { style: { opacity: 0.7, margin: "0.25rem 0 0" } }, "Deadline: " + period.endDate)',
    '      )',
    '    ),',
    '    h("div", { style: { display: "flex", gap: "1rem", margin: "1.5rem 0" } },',
    '      h("button", { onClick: function() { navigate("/catalog") }, style: { padding: "0.75rem 1.5rem", background: "#1565c0", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 14 } }, "Browse Catalog"),',
    '      h("button", { onClick: function() { navigate("/my-orders") }, style: { padding: "0.75rem 1.5rem", background: "#e0e0e0", color: "#333", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 14 } }, "My Orders")',
    '    ),',
    '    h("div", { style: { margin: "2rem 0" } },',
    '      h("h3", null, "How to Place an Order"),',
    '      h("ol", { style: { paddingLeft: "1.5rem", lineHeight: 1.8, listStyle: "decimal" } },',
    '        h("li", null, "Browse the Catalog and select items"),',
    '        h("li", null, "Items are added to your draft order"),',
    '        h("li", null, "Review your order and select ship-to location"),',
    '        h("li", null, "Click Place Order to submit (points deducted)"),',
    '        h("li", null, "Your approver will review and approve"),',
    '        h("li", null, "You\'ll be notified when your order ships")',
    '      )',
    '    ),',
    '    categories.length ? h("div", { style: { margin: "2rem 0" } },',
    '      h("h3", null, "Browse by Category"),',
    '      h("div", { style: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "1rem" } },',
    '        categories.map(function(cat) {',
    '          return h("div", { key: cat.id, onClick: function() { navigate("/catalog") }, style: { padding: "1rem", border: "1px solid #e0e0e0", borderRadius: 8, cursor: "pointer", textAlign: "center" } },',
    '            h("p", { style: { fontWeight: 500, margin: 0 } }, cat.name),',
    '            cat.description && h("p", { style: { fontSize: 12, opacity: 0.7, margin: "0.25rem 0 0" } }, cat.description)',
    '          )',
    '        })',
    '      )',
    '    ) : null,',
    '    h("div", { style: { marginTop: "2rem", padding: "1rem", background: "#f5f5f5", borderRadius: 8 } },',
    '      h("h3", { style: { margin: "0 0 0.5rem" } }, "Need Help?"),',
    '      h("p", { style: { margin: 0 } }, "Call toll-free: 1-800-" + (app.project && app.project.name || "HELP").toUpperCase()),',
    '      h("p", { style: { margin: "0.25rem 0 0" } }, "Email: uniforms@" + (app.project && app.project.name || "support").toLowerCase() + ".ca")',
    '    )',
    '  )',
    '}',
  ].join('\n') });

  // Pending approvals dashboard (visible only to Approvers and Admins)
  const pendingApprovals = await orderingMetaDb.create(P, { name: 'pendingApprovals', type: 'custom', code: [
    'function({ React, app, navigate }) {',
    '  var h = React.createElement',
    '  var _s = React.useState(null)',
    '  var account = _s[0], setAccount = _s[1]',
    '  var _s2 = React.useState([])',
    '  var pending = _s2[0], setPending = _s2[1]',
    '  React.useEffect(function() {',
    '    app.database.get("/users", app.user.id).then(function(a) {',
    '      setAccount(a)',
    '      if (a.role === "Approver" || a.role === "Admin") {',
    '        app.database.list("/orders", ["user"], { status: "Placed" }).then(function(orders) {',
    '          if (a.role === "Approver") {',
    '            orders = orders.filter(function(o) { return o.user && o.user.department === a.department })',
    '          }',
    '          setPending(orders)',
    '        })',
    '      }',
    '    })',
    '  }, [])',
    '  if (!account || (account.role !== "Approver" && account.role !== "Admin")) return null',
    '  if (!pending.length) return null',
    '  return h("div", { style: { marginTop: "2rem", width: "100%" } },',
    '    h("h3", { style: { margin: "0 0 1rem" } }, "Pending Approvals (" + pending.length + ")"),',
    '    h("table", { style: { width: "100%", borderCollapse: "collapse" } },',
    '      h("thead", null,',
    '        h("tr", null,',
    '          h("th", { style: { textAlign: "left", padding: "0.5rem", borderBottom: "2px solid #e0e0e0" } }, "User"),',
    '          h("th", { style: { textAlign: "left", padding: "0.5rem", borderBottom: "2px solid #e0e0e0" } }, "Total Points"),',
    '          h("th", { style: { textAlign: "left", padding: "0.5rem", borderBottom: "2px solid #e0e0e0" } }, "Order Date")',
    '        )',
    '      ),',
    '      h("tbody", null,',
    '        pending.map(function(o) {',
    '          return h("tr", { key: o.id, onClick: function() { navigate("/orders/" + o.id) }, style: { cursor: "pointer" } },',
    '            h("td", { style: { padding: "0.5rem", borderBottom: "1px solid #e0e0e0" } }, o.user && o.user.name || "Unknown"),',
    '            h("td", { style: { padding: "0.5rem", borderBottom: "1px solid #e0e0e0" } }, o.totalPointCost || 0),',
    '            h("td", { style: { padding: "0.5rem", borderBottom: "1px solid #e0e0e0" } }, o.orderDate || "")',
    '          )',
    '        })',
    '      )',
    '    )',
    '  )',
    '}',
  ].join('\n') });

  // ── Phase 3: Update models with property IDs and action IDs ──

  const userPropIds = [userName.id, userEmail.id, userPic.id, userActive.id, staffPointsAllotment.id, staffPointsSpent.id, staffPointsRemaining.id, staffDepartment.id, staffLocations.id, staffApprover.id, staffRole.id];
  const orderPropIds = [orderUser.id, orderItems.id, orderTotalPointCost.id, orderStatus.id, orderDate.id, orderShipTo.id, orderNotes.id, orderTrackingNumber.id];
  const orderItemPropIds = [oiCatalogItem.id, oiQuantity.id, oiSize.id, oiPointCost.id, oiLogoChoice.id];

  await Promise.all([
    orderingMetaDb.update(M, orderingHomeModel.id, { properties: [orderingWelcome.id, pendingApprovals.id] }),
    orderingMetaDb.update(M, orderingUserModel.id, { properties: userPropIds, actions: usersActionIds }),
    orderingMetaDb.update(M, categoriesModel.id, { properties: [catName.id, catDescription.id], actions: categoriesCrud }),
    orderingMetaDb.update(M, catalogModel.id, { properties: [catalogName.id, catalogDescription.id, catalogCategory.id, catalogPointCost.id, catalogImage.id, catalogSizes.id, catalogActive.id, catalogStockQuantity.id, catalogInStock.id, catalogBackorderDate.id, catalogDepartments.id, catalogLogoOptions.id], actions: catalogActionIds }),
    orderingMetaDb.update(M, locationsModel.id, { properties: [locName.id, locAddress.id], actions: locationsCrud }),
    orderingMetaDb.update(M, ordersModel.id, { properties: orderPropIds, actions: ordersActionIds }),
    orderingMetaDb.update(M, orderItemsModel.id, { properties: orderItemPropIds, actions: orderItemsActionIds }),
    orderingMetaDb.update(M, myAccountModel.id, { properties: [userName.id, userEmail.id, userPic.id, staffPointsAllotment.id, staffPointsSpent.id, staffPointsRemaining.id, staffDepartment.id, staffLocations.id, staffApprover.id, staffRole.id], actions: myAccountActionIds }),
    orderingMetaDb.update(M, orderPeriodsModel.id, { properties: [periodName.id, periodStartDate.id, periodEndDate.id, periodActive.id], actions: orderPeriodsCrud }),
    orderingMetaDb.update(M, myOrdersModel.id, { properties: [myOrderUser.id, orderItems.id, orderTotalPointCost.id, orderStatus.id, orderDate.id, orderShipTo.id, orderNotes.id, orderTrackingNumber.id], actions: myOrdersActionIds }),
  ]);

  // ── Phase 4: Entity data ──

  // Projects
  const [bramptonProject, vancouverProject] = await Promise.all([
    orderingMetaDb.create(projectModel as unknown as Model, { name: 'BRAMPTON', url: 'brampton', color: '#1565c0', logo: '', design: 'shadcn', tier: 'startup', database: 'local', parent: orderingProjectId }),
    orderingMetaDb.create(projectModel as unknown as Model, { name: 'Vancouver', url: 'vancouver', color: '#2e7d32', logo: '', design: 'shadcn', tier: 'startup', database: 'local', parent: orderingProjectId }),
  ]);

  // User entities (matching root users for group membership + Mary)
  for (const u of rootUsers) {
    orderingMetaDb.create(orderingUserModel as unknown as Model, {
      id: u.id, name: u.name, email: u.email, location: u.location, pic: u.pic, active: u.active,
    });
  }
  const mary = await orderingMetaDb.create(orderingUserModel as unknown as Model, {
    name: 'Mary', email: 'mary@example.com',
    pic: 'https://randomuser.me/api/portraits/women/3.jpg', active: true,
  });
  const [sammy, sam, ester, samuca] = rootUsers;

  // Groups
  const bramptonEndUsers = await orderingMetaDb.create(groupModel as unknown as Model, { name: 'Brampton End Users', users: [sammy.id] });
  const bramptonApprovers = await orderingMetaDb.create(groupModel as unknown as Model, { name: 'Brampton Approvers', users: [sam.id] });
  const bramptonAdmins = await orderingMetaDb.create(groupModel as unknown as Model, { name: 'Brampton Admins', users: [ester.id] });
  const vancouverEndUsers = await orderingMetaDb.create(groupModel as unknown as Model, { name: 'Vancouver End Users', users: [sammy.id, sam.id] });
  const vancouverApprovers = await orderingMetaDb.create(groupModel as unknown as Model, { name: 'Vancouver Approvers', users: [ester.id] });
  const vancouverAdmins = await orderingMetaDb.create(groupModel as unknown as Model, { name: 'Vancouver Admins', users: [samuca.id] });

  // Capabilities — "all" for each domain model + users
  const allCaps = new Map<string, string>();
  for (const model of [...allDomainModels, orderingUserModel]) {
    const cap = await orderingMetaDb.create(capabilityModel as unknown as Model, { action: 'all', model: model.id });
    allCaps.set(model.id, cap.id);
  }
  // Capabilities — "read" for reference models
  const readCaps = new Map<string, string>();
  for (const model of readModels) {
    const cap = await orderingMetaDb.create(capabilityModel as unknown as Model, { action: 'read', model: model.id });
    readCaps.set(model.id, cap.id);
  }

  // Helper: authorize a role on a project
  const authorize = (group: string, capability: string, project: string, condition?: string) =>
    orderingMetaDb.create(authorizationModel as unknown as Model, { group, capability, project, ...(condition ? { condition } : {}) });

  // Helper: authorize standard end-user / approver role
  const authorizeStandardRole = (group: string, project: string, orderCondition?: string) => {
    authorize(group, allCaps.get(orderingHomeModel.id)!, project);
    for (const model of readModels) {
      authorize(group, readCaps.get(model.id)!, project);
    }
    authorize(group, allCaps.get(ordersModel.id)!, project, orderCondition);
    authorize(group, allCaps.get(orderItemsModel.id)!, project);
    authorize(group, allCaps.get(myAccountModel.id)!, project);
    authorize(group, allCaps.get(myOrdersModel.id)!, project);
    authorize(group, allCaps.get(orderingUserModel.id)!, project);
  };

  // Helper: authorize admin role (all caps on all models)
  const authorizeAdmin = (group: string, project: string) => {
    for (const model of [...allDomainModels, orderingUserModel]) {
      authorize(group, allCaps.get(model.id)!, project);
    }
  };

  // -- Brampton --
  authorizeAdmin(bramptonAdmins.id, bramptonProject.id);
  authorizeStandardRole(bramptonEndUsers.id, bramptonProject.id, 'return { user: app.user.id }');
  authorizeStandardRole(bramptonApprovers.id, bramptonProject.id);

  // -- Vancouver --
  authorizeAdmin(vancouverAdmins.id, vancouverProject.id);
  authorizeStandardRole(vancouverEndUsers.id, vancouverProject.id, 'return { user: app.user.id }');
  authorizeStandardRole(vancouverApprovers.id, vancouverProject.id);

  void mary;

  return {
    models: { orderingUserModel, categoriesModel, catalogModel, locationsModel, ordersModel, orderPeriodsModel, orderItemsModel },
    bramptonProjectId: bramptonProject.id,
    vancouverProjectId: vancouverProject.id,
  };
}
